import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionsContext";
import {
  LayoutDashboard,
  Users,
  UserCog,
  Calendar,
  FileText,
  Settings,
  LogOut,
  UserCircle,
  Menu,
  X,
  Bell,
  Search,
  ChevronDown,
  Activity,
  Building2,
  Stethoscope,
  ClipboardList,
  ClipboardCheck,
  MessageSquare,
  DollarSign,
  Fingerprint,
  BarChart3,
  AlertTriangle,
  CheckCircle,
  Info,
  Clock,
  GraduationCap,
  Printer,
  PrinterCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation items with permission mappings
const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, permission: "dashboard" },
  { path: "/clients", label: "Clients", icon: Users, permission: "clients" },
  { path: "/employees", label: "Employees", icon: UserCog, permission: "employees" },
  { path: "/physicians", label: "Physicians", icon: Stethoscope, permission: "physicians" },
  { path: "/schedule", label: "Schedule", icon: Calendar, permission: "schedule" },
  { path: "/evv", label: "EVV", icon: Fingerprint, permission: "evv" },
  { path: "/referrals", label: "Referrals", icon: FileText, permission: "referrals" },
  { path: "/order-tracking", label: "Order Tracking", icon: ClipboardList, permission: "order_tracking" },
  { path: "/communication", label: "Communication", icon: MessageSquare, permission: "communications" },
  { path: "/faxes", label: "Faxes", icon: Printer, permission: "communications" },
  { path: "/hr-payroll", label: "HR & Payroll", icon: DollarSign, permission: "hr_payroll" },
  { path: "/print-queue", label: "Print Queue", icon: PrinterCheck, permission: "settings", adminOnly: true },
  { path: "/rn-signatures", label: "RN Signatures", icon: Stethoscope, permission: "dashboard", roles: ["rn", "admin", "case_manager", "hr_scheduler", "scheduler"] },
  { path: "/rn-assessments", label: "RN Assessments", icon: ClipboardCheck, permission: "dashboard", roles: ["rn", "admin", "case_manager", "hr_scheduler", "scheduler", "intake_coordinator"] },
  { path: "/evaluation-reports", label: "Eval Reports", icon: BarChart3, permission: "evaluations" },
  { path: "/training", label: "Training", icon: GraduationCap, permission: "dashboard" },
  { path: "/platform", label: "Platform", icon: Building2, permission: "dashboard", platformOnly: true },
  { path: "/settings", label: "Settings", icon: Settings, permission: "settings" },
];

// Roles that can do field visits and need access to clock in/out
const fieldVisitRoles = ["rn", "pcw", "case_manager"];

const roleLabels = {
  admin: "Administrator",
  case_manager: "Case Manager",
  hr_scheduler: "HR/Scheduler",
  evv_specialist: "EVV Specialist",
  rn: "Registered Nurse",
  pcw: "Personal Care Worker",
  intake_coordinator: "Intake Coordinator",
  receptionist: "Receptionist",
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { hasPermission, isAdmin } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [unreadFaxCount, setUnreadFaxCount] = useState(0);
  const [printQueueCount, setPrintQueueCount] = useState(0);

  // Poll for pending print jobs so we can badge the Print Queue nav item (admin only)
  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API}/print-queue/count`);
        if (!cancelled) setPrintQueueCount(res.data?.count || 0);
      } catch (e) {
        // silent
      }
    };
    fetchCount();
    const t = setInterval(fetchCount, 30_000);
    const onChange = () => fetchCount();
    window.addEventListener("print-queue-changed", onChange);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener("print-queue-changed", onChange);
    };
  }, [isAdmin]);

  // Poll for unread incoming fax count so we can badge the Faxes nav item
  useEffect(() => {
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const res = await axios.get(`${API}/fax/incoming/unread-count`);
        if (!cancelled) setUnreadFaxCount(res.data?.count || 0);
      } catch (e) {
        // silent — 401 during logout / new sessions is fine
      }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Fetch dashboard alerts for the bell icon
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setAlertsLoading(true);
        const response = await axios.get(`${API}/dashboard/stats`);
        if (response.data?.alerts) {
          setAlerts(response.data.alerts);
        }
      } catch (error) {
        console.error("Failed to fetch alerts:", error);
      } finally {
        setAlertsLoading(false);
      }
    };
    
    fetchAlerts();
    // Refresh alerts every 2 minutes
    const interval = setInterval(fetchAlerts, 120000);
    return () => clearInterval(interval);
  }, []);

  const unacknowledgedAlerts = alerts.filter(a => !a.acknowledged);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getAlertIcon = (severity) => {
    switch (severity) {
      case 'high':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'medium':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      default:
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  // Filter nav items based on permissions
  const visibleNavItems = navItems.filter((item) => {
    if (item.platformOnly) return !!user?.is_platform_owner;
    if (item.adminOnly && !isAdmin) return false;
    if (item.roles && !item.roles.includes((user?.role || "").toLowerCase())) return false;
    // Always show dashboard
    if (item.permission === "dashboard") return true;
    // Check permission for the feature
    return hasPermission(item.permission);
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {localStorage.getItem("platform_owner_token") && (
        <div className="sticky top-0 z-[60] bg-amber-400 text-amber-950 text-sm px-4 py-1.5 flex items-center justify-center gap-3" data-testid="impersonation-banner">
          <span>Viewing as <strong>{localStorage.getItem("platform_impersonating") || "agency"}</strong> (platform owner session)</span>
          <button
            type="button"
            className="underline font-medium"
            data-testid="impersonation-return-btn"
            onClick={() => {
              localStorage.setItem("token", localStorage.getItem("platform_owner_token"));
              localStorage.removeItem("platform_owner_token");
              localStorage.removeItem("platform_impersonating");
              window.location.href = "/platform";
            }}
          >
            Return to Platform
          </button>
        </div>
      )}
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`sidebar transform transition-transform duration-200 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        {/* Logo */}
        <div className="p-4 bg-white border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center justify-center w-full">
            <img 
              src="/carepilot-logo.png" 
              alt="CarePilot" 
              className="h-36 w-auto"
            />
          </Link>
          <button
            className="md:hidden ml-auto p-1 hover:bg-slate-100 rounded absolute top-4 right-4"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Agency Info */}
        <div className="px-4 py-3 border-b border-slate-200 bg-blue-50">
          <div className="flex items-center gap-2 text-sm">
            <Building2 className="w-4 h-4 text-blue-600" />
            <span className="text-blue-800 font-medium truncate">{user?.agency_name || "Your Agency"}</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
            
            if (item.disabled) {
              return (
                <div
                  key={item.path}
                  className="nav-item opacity-50 cursor-not-allowed"
                  title="Coming soon"
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </div>
              );
            }
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive ? "active" : ""}`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.path.replace(/^\//, "") || "root"}`}
              >
                <Icon className="w-5 h-5" />
                <span className="flex-1">{item.label}</span>
                {item.path === "/faxes" && unreadFaxCount > 0 && (
                  <span
                    className="ml-auto inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-semibold leading-none text-white animate-pulse"
                    title={`${unreadFaxCount} unread fax${unreadFaxCount === 1 ? "" : "es"}`}
                    data-testid="nav-faxes-unread-badge"
                  >
                    {unreadFaxCount > 99 ? "99+" : unreadFaxCount}
                  </span>
                )}
                {item.path === "/print-queue" && printQueueCount > 0 && (
                  <span
                    className="ml-auto inline-flex min-w-[1.25rem] h-5 items-center justify-center rounded-full bg-teal-600 px-1.5 text-[10px] font-semibold leading-none text-white"
                    title={`${printQueueCount} document${printQueueCount === 1 ? "" : "s"} ready to print`}
                    data-testid="nav-print-queue-badge"
                  >
                    {printQueueCount > 99 ? "99+" : printQueueCount}
                  </span>
                )}
              </Link>
            );
          })}
          
          {/* Field Mode - For roles that do field visits (RN, Case Manager, etc.) */}
          {fieldVisitRoles.includes(user?.role) && (
            <>
              <div className="border-t border-slate-200 my-2" />
              <Link
                to="/caregiver"
                className={`nav-item ${location.pathname === "/caregiver" ? "active" : ""} bg-teal-50 text-teal-700 hover:bg-teal-100`}
                onClick={() => setSidebarOpen(false)}
                data-testid="field-mode-link"
              >
                <Clock className="w-5 h-5" />
                <span>Field Mode</span>
              </Link>
            </>
          )}
        </nav>

        {/* Sidebar Footer */}
        <div className="sidebar-footer space-y-1">
          {hasPermission("settings") && (
            <Link
              to="/settings"
              className={`nav-item ${location.pathname === "/settings" ? "active" : ""}`}
              onClick={() => setSidebarOpen(false)}
            >
              <Settings className="w-5 h-5" />
              <span>Settings</span>
            </Link>
          )}
          <button
            onClick={handleLogout}
            className="nav-item w-full text-red-600 hover:bg-red-50 hover:text-red-700"
            data-testid="sidebar-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        {/* Topbar */}
        <header className="topbar">
          <button
            className="mobile-menu-toggle"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-toggle"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Search */}
          <div className="hidden md:flex items-center flex-1 max-w-md">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search clients, employees..."
                className="w-full pl-10 pr-4 py-2 text-sm bg-slate-100 border-0 rounded-lg focus:bg-white focus:ring-2 focus:ring-primary/20 transition-colors"
                data-testid="global-search"
              />
            </div>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-4 ml-auto">
            {/* Notifications/Alerts Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  data-testid="notifications-btn"
                >
                  <Bell className="w-5 h-5 text-slate-600" />
                  {unacknowledgedAlerts.length > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="px-3 py-2 border-b border-slate-100">
                  <p className="text-sm font-semibold text-slate-900">Alerts</p>
                  <p className="text-xs text-slate-500">
                    {unacknowledgedAlerts.length > 0 
                      ? `${unacknowledgedAlerts.length} unread alert${unacknowledgedAlerts.length > 1 ? 's' : ''}`
                      : 'No new alerts'}
                  </p>
                </div>
                {alertsLoading ? (
                  <div className="px-3 py-4 text-center">
                    <p className="text-sm text-slate-500">Loading...</p>
                  </div>
                ) : alerts.length === 0 ? (
                  <div className="px-3 py-4 text-center">
                    <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">All caught up!</p>
                    <p className="text-xs text-slate-400">No alerts at this time</p>
                  </div>
                ) : (
                  <div className="max-h-72 overflow-y-auto">
                    {alerts.slice(0, 5).map((alert, index) => (
                      <DropdownMenuItem 
                        key={index}
                        className="px-3 py-2 cursor-pointer"
                        onClick={() => alert.link && navigate(alert.link)}
                      >
                        <div className="flex items-start gap-3 w-full">
                          {getAlertIcon(alert.severity)}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${alert.acknowledged ? 'text-slate-500' : 'font-medium text-slate-900'}`}>
                              {alert.title}
                            </p>
                            <p className="text-xs text-slate-500 truncate">{alert.description}</p>
                          </div>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </div>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  className="px-3 py-2 cursor-pointer justify-center"
                  onClick={() => navigate('/dashboard')}
                >
                  <span className="text-sm text-primary font-medium">View all alerts</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="flex items-center gap-2 hover:bg-slate-100 rounded-lg p-1.5 transition-colors"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-slate-700">{user?.name}</p>
                    <p className="text-xs text-slate-500">{roleLabels[user?.role] || user?.role}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400 hidden md:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                {!["pcw", "caregiver", "care_worker", "personal_care_worker"].includes((user?.role || "").toLowerCase()) && (
                  <DropdownMenuItem onClick={() => navigate("/settings?tab=security")} data-testid="edit-profile-btn">
                    <UserCircle className="w-4 h-4 mr-2" />
                    Edit my profile
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="main-content-inner">{children}</main>
      </div>
    </div>
  );
};
